import { useEffect, useMemo, useRef, useState } from "react";

import { create } from "@bufbuild/protobuf";
import { useQuery } from "@tanstack/react-query";

import { GetPresignedDownloadUriRequestSchema } from "@gx/ts-contracts/services/source/api/source_pb";

import { BrowserbaseReplayEvent } from "@/pages/sources/components/edit/tabs/syncs/browserbase/types";
import { fetchRecording } from "@/pages/sources/components/edit/tabs/syncs/browserbase/utils";

import { useGetPresignedDownloadUriQuery } from "@/api/queries/sources";

export function useBrowserbaseRecording(sessionRecordingUri: string | null | undefined) {
  const presignedQuery = useGetPresignedDownloadUriQuery({
    input: create(GetPresignedDownloadUriRequestSchema, {
      objectStorageUri: sessionRecordingUri ?? "",
    }),
    options: {
      enabled: !!sessionRecordingUri,
      staleTime: Infinity,
      gcTime: Infinity,
    },
  });

  const downloadUri = presignedQuery.data?.downloadUri;
  const recordingQuery = useQuery<BrowserbaseReplayEvent[]>({
    queryKey: ["browserbase-recording", downloadUri],
    queryFn: () => fetchRecording(downloadUri!),
    enabled: !!downloadUri,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return {
    events: recordingQuery.data,
    isLoading: !!downloadUri && (presignedQuery.isLoading || recordingQuery.isLoading),
    isError: presignedQuery.isError || recordingQuery.isError,
    isEmpty: recordingQuery.data?.length === 0,
  };
}

export function useBrowserbaseLiveviewIframeDisconnect(liveViewLink: string, syncId: string) {
  const [hasDisconnected, setHasDisconnected] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const origin = useMemo(() => {
    try {
      return liveViewLink ? new URL(liveViewLink).origin : null;
    } catch {
      return null;
    }
  }, [liveViewLink]);

  useEffect(() => {
    setHasDisconnected(false);
  }, [liveViewLink, syncId]);

  useEffect(() => {
    if (!liveViewLink) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data !== "browserbase-disconnected") return;
      if (origin && event.origin !== origin) return;
      if (event.source && event.source !== iframeRef.current?.contentWindow) return;
      setHasDisconnected(true);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [liveViewLink, origin]);

  return { hasDisconnected, iframeRef };
}
